import Vue from 'vue';
import set from "lodash-es/set";
import { get } from "lodash-es";
import { toPathParts } from '@tencent/data-schema-core/lib/utils/path';

/**
 * 众所周知，Vue 里需要用烦人的 Vue.set 来处理赋值，但是对于深层的对象就显得很麻烦
 *
 * 所以这个方法可以提供这个便利：
 * - 全部使用 Vue.set 赋值
 * - 自动处理 obj 里空对象的情况
 * - 如果 sth 里存在值为 undefined 的东西，会去删除对于 obj 里的东西
 */
export function vueDeepAssign(obj, sth) {
  if (!obj || !sth) return;
  Object.keys(sth).forEach(key => {
    const origin = obj[key];
    const value = sth[key];
    const valueIsUndefined = typeof value === 'undefined';
    const originIsUndefined = typeof origin === 'undefined';

    // undefined 表示需要删除
    if (valueIsUndefined && !originIsUndefined) Vue.delete(obj, key);
    if (!valueIsUndefined && originIsUndefined) Vue.set(obj, key, value);
    if (!valueIsUndefined && !originIsUndefined) {
      // 需要深层处理？
      const originIsObject = typeof origin === 'object' && origin !== null;
      const valueIsObject = typeof value === 'object' && value !== null;
      if (originIsObject && valueIsObject) vueDeepAssign(origin, value);else Vue.set(obj, key, value);
    }
  });
}

/**
 * 众所周知，Vue 的 Vue.set 第二个变量，不支持 `foo.bar` 这种方式深层赋值。
 * 而直接使用 lodash 的 set 会导致中间某些层未经 Vue.set 而丢失 reactive
 *
 * 这个就是给 Vue 量身定做的 lodash.set
 */
export function vueDeepSet(obj, path, value) {
  const parts = toPathParts(path);
  let ptr = obj;
  let prevPtr = null;
  let nonNullKeyIdx = 0;
  let keyOfPtr = '';
  for (nonNullKeyIdx = 0; nonNullKeyIdx < parts.length; nonNullKeyIdx++) {
    keyOfPtr = parts[nonNullKeyIdx];
    prevPtr = ptr;
    ptr = ptr[keyOfPtr];

    // eslint-disable-next-line eqeqeq
    if (ptr == null) break;
  }
  if (nonNullKeyIdx >= parts.length) {
    // foo.bar.baz 可以确保 foo.bar 都是存在的，安全的直接在目标字段 set 即可
    Vue.set(prevPtr, keyOfPtr, value);
  } else {
    // 中间出现了断层
    const filled = {};
    set(filled, ['temp', ...parts.slice(nonNullKeyIdx + 1)], value);
    Vue.set(prevPtr, keyOfPtr, filled.temp);
  }
}

/** 按照path删除 如果删除的路径上对象为空了就一块删掉 */
export function vueDeepDelete(obj, path) {
  if (!obj) return;
  const parts = toPathParts(path);
  if (parts.length === 0) return;
  const firstPath = parts[0];
  const objAtPath0 = get(obj, firstPath);
  if (parts.length > 1) {
    vueDeepDelete(objAtPath0, parts.slice(1));
  }
  if (parts.length === 1) {
    // 是最后一个元素 直接执行delete
    Vue.delete(obj, firstPath);
  } else if (parts.length > 1) {
    // 否则前面已经递归搞定了[1,n]个元素下面看对象是否为空
    if (objAtPath0 instanceof Object && Object.keys(objAtPath0).length === 0) {
      Vue.delete(obj, firstPath);
    }
  }
}

/** 在obj上 找到xxx.yyy.zzz最后一个可见的对象位置
 * @param obj 对象
 * @pathPart ['xxx','yyy','zzz']
 * @returns {fullPathSafe:boolean,prevPtr:any,keyOfPtr:string,nonNullKeyIdx:number}
 * */
function getNotNullLastPtr(obj, pathPart) {
  let ptr = obj;
  let prevPtr = null;
  let nonNullKeyIdx = 0;
  let keyOfPtr = '';
  for (nonNullKeyIdx = 0; nonNullKeyIdx < pathPart.length; nonNullKeyIdx++) {
    keyOfPtr = pathPart[nonNullKeyIdx];
    prevPtr = ptr;
    ptr = ptr[keyOfPtr];

    // eslint-disable-next-line eqeqeq
    if (ptr == null) break;
  }
  const fullPathSafe = nonNullKeyIdx >= pathPart.length;
  return {
    prevPtr,
    nonNullKeyIdx,
    fullPathSafe,
    keyOfPtr
  };
}

/** 给定路径foo.bar.baz后 移除baz */
export function vueDeepDeleteLast(obj, path) {
  const parts = toPathParts(path);
  const {
    prevPtr,
    fullPathSafe,
    keyOfPtr
  } = getNotNullLastPtr(obj, parts);
  if (fullPathSafe) {
    // foo.bar.baz 可以确保 foo.bar 都是存在的，安全的直接delete
    Vue.delete(prevPtr, keyOfPtr);
    return true;
  }
  return false;
}
//# sourceMappingURL=vueDeepAssign.js.map
